'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';

export type AdSenseFormat = 'auto' | 'rectangle' | 'horizontal' | 'vertical';

interface AdSenseUnitProps {
  adSlot: string;
  adFormat?: AdSenseFormat;
  className?: string;
  label?: string;
}

/**
 * Renders a single Google AdSense ad unit.
 * Requires NEXT_PUBLIC_ADSENSE_ID to be set in .env
 */
const AdSenseUnit: React.FC<AdSenseUnitProps> = ({
  adSlot,
  adFormat = 'auto',
  className = '',
  label = 'Advertisement',
}) => {
  const adRef = useRef<HTMLInsElement>(null);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(false);

  const adClient = process.env.NEXT_PUBLIC_ADSENSE_ID;

  const initAd = useCallback(() => {
    if (initialized || error || !adRef.current || !adClient || adClient === 'your-adsense-id-here') return;
    if (typeof window === 'undefined' || !window.adsbygoogle) return;

    const el = adRef.current;
    if (el.getAttribute('data-adsbygoogle-status')) {
      setInitialized(true);
      return;
    }

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      setInitialized(true);
    } catch (e) {
      console.warn('AdSense init error:', e);
      setError(true);
    }
  }, [initialized, error, adClient]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (window.adsbygoogle) {
        initAd();
      } else {
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (window.adsbygoogle) {
            clearInterval(interval);
            initAd();
          } else if (attempts > 40) {
            clearInterval(interval);
            setError(true);
          }
        }, 150);
        return () => clearInterval(interval);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [initAd]);

  // Don't render if no valid AdSense ID
  if (!adClient || adClient === 'your-adsense-id-here') return null;

  if (error) {
    return (
      <div className={`w-full text-center py-3 ${className}`}>
        <span className="text-[10px] uppercase tracking-widest text-text-muted opacity-50">{label}</span>
      </div>
    );
  }

  return (
    <div className={`w-full overflow-hidden ${className}`}>
      <p className="text-[9px] uppercase tracking-widest text-text-muted text-center mb-1 opacity-60">{label}</p>
      <ins
        ref={adRef}
        className="adsbygoogle block w-full"
        style={{ display: 'block', minHeight: '90px' }}
        data-ad-client={adClient}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default AdSenseUnit;
